import { createSignal, createEffect, onMount, onCleanup } from 'solid-js';
import type { BreadcrumbItem } from '~/components/common/Breadcrumb';

export function useResponsiveBreadcrumbs(
  breadcrumbItems: () => BreadcrumbItem[],
  containerRef: () => HTMLDivElement | null,
  currentPath: () => string,
  mobileVariant: () => 'rootOnly' | 'currentOnly' = () => 'currentOnly'
) {
  const [visibleBreadcrumbs, setVisibleBreadcrumbs] = createSignal<BreadcrumbItem[]>([]);

  function getTextWidth(text: string, font = '14px Poppins, Arial, sans-serif'): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('2D context not supported or canvas already in use');
    }

    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  async function updateVisibleBreadcrumbs() {
    const items = breadcrumbItems();

    if (items.length < 2) {
      setVisibleBreadcrumbs(items);
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const middle = items.slice(1, -1);

    const windowWidth = window.innerWidth;
    const container = containerRef();
    const parentNode = container?.parentElement?.parentElement;
    const lis = container?.getElementsByClassName('real-li');

    const containerWidth = parentNode ? parentNode.offsetWidth : container?.offsetWidth || 0;

    let availableWidth: number;
    if (windowWidth <= 640) {
      availableWidth = containerWidth - 300;

      if (mobileVariant() === 'currentOnly') {
        await setVisibleBreadcrumbs([last]);
      } else {
        const parentOfLast = items.length >= 2 ? items[items.length - 2] : first;
        await setVisibleBreadcrumbs([parentOfLast]);
      }
      return;
    } else if (windowWidth <= 1024) {
      availableWidth = containerWidth - 180;
    } else if (windowWidth <= 1280) {
      availableWidth = containerWidth - 430;
    } else {
      availableWidth = containerWidth - 540;
    }

    let sideElementWidth = 0;
    sideElementWidth = getTextWidth(items[0].label) + getTextWidth(items[items.length - 1].label) + 20 * 4 + 55;

    availableWidth = availableWidth - sideElementWidth;

    const visibleMiddle: BreadcrumbItem[] = [];
    let totalMiddleWidth = 0;

    for (const [index, item] of middle.entries()) {
      totalMiddleWidth += getTextWidth(item.label);

      if (totalMiddleWidth < availableWidth) {
        visibleMiddle.push(item);
      } else {
        const hiddenLabels = middle
          .slice(index)
          .map((i) => i.label)
          .join(' / ');
        visibleMiddle.push({
          label: '...',
          link: currentPath(),
          title: hiddenLabels,
          linkClass: 'text-text-03 cursor-default',
        });
        break;
      }
    }

    await setVisibleBreadcrumbs([first, ...visibleMiddle, last]);

    if (!lis || !lis.length || !lis[lis.length - 1]) {
      return;
    }
  }

  createEffect(() => {
    breadcrumbItems();
    currentPath();
    updateVisibleBreadcrumbs();
  });

  onMount(() => {
    updateVisibleBreadcrumbs();
    const observer = new ResizeObserver(() => {
      updateVisibleBreadcrumbs();
    });

    const container = containerRef();
    if (container) {
      observer.observe(document.body);
    }
    onCleanup(() => observer.disconnect());
  });

  return {
    visibleBreadcrumbs,
    updateVisibleBreadcrumbs,
  };
}
